import React, { ReactNode, useEffect, useMemo, useState } from "react";
import styled from "@emotion/styled";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Typography, TypographyProps } from "@mui/material";
import { useTranslation } from "react-i18next";
import {
  AccountSelectGroup,
  AccountsApiListAndSearchAllAccountsRequest,
  AccountsApiSelectGroupsIdRequest,
  PublicAccount,
} from "@rentablo/api/apiv1";

const DepotSelectorIcon = styled(KeyboardArrowDownRoundedIcon)`
  color: currentColor;
`;

export type Props = {
  accounts?: PublicAccount[];
  selectedGroup?: AccountSelectGroup;
  accountParams?: AccountsApiListAndSearchAllAccountsRequest;
  selectedGroupParams: AccountsApiSelectGroupsIdRequest;
  color?: string;
  textKeys?: {
    allDepots: string;
    amountDepots: string;
    noDepots: string;
  };
  icon?: ReactNode;
  isOpen: boolean;
  onButtonClick: () => void;
} & TypographyProps;

export const DepotsSelectorButton = ({
  accounts,
  selectedGroup,
  accountParams,
  selectedGroupParams,
  color = "text.primary",
  textKeys = {
    allDepots: "depotsSelector.allDepots",
    amountDepots: "depotsSelector.amountDepots",
    noDepots: "depotsSelector.noDepots",
  },
  icon,
  onButtonClick,
  isOpen,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const [checkedPortfolios, setCheckedPortfolios] = useState<number[]>(
    selectedGroup?.accountIds || []
  );

  const selectableAccounts = useMemo(
    () =>
      accounts?.filter(
        (account) =>
          !account.depotIds?.length ||
          !accounts?.some((acc) => account.depotIds?.includes(acc.id))
      ) ?? [],
    [accounts]
  );

  const amountSelectableAccounts = selectableAccounts?.length ?? 0;
  const amountSelectedAccounts =
    selectableAccounts?.filter((x) => checkedPortfolios.includes(x.id))
      .length ?? 0;

  const depotsSelectorText =
    amountSelectedAccounts === amountSelectableAccounts
      ? t(textKeys.allDepots)
      : t(textKeys.amountDepots, {
          selected: amountSelectedAccounts,
          total: amountSelectableAccounts,
        });

  useEffect(() => {
    if (selectedGroup?.accountIds) {
      const filteredAccounts = selectedGroup.accountIds.filter((id) =>
        accounts?.some((x) => x.id === id)
      );
      // eslint-disable-next-line react-hooks/set-state-in-effect -- sync from prop (filtered)
      setCheckedPortfolios(filteredAccounts);
    }
  }, [selectedGroup, isOpen, accounts]);

  return (
    <Box
      onClick={() => Boolean(amountSelectableAccounts) && onButtonClick()}
      data-testid={
        amountSelectableAccounts ? "select-depots" : "no-select-depots"
      }
    >
      <Box
        display="inline-flex"
        alignItems="center"
        color="currentColor"
        sx={{
          height: "27px",
          cursor: "pointer",
        }}
      >
        {amountSelectableAccounts !== 0 ? (
          <>
            <Typography variant="body4" {...rest}>
              {!amountSelectedAccounts
                ? t(textKeys.allDepots)
                : depotsSelectorText}
            </Typography>
            {icon ? icon : <DepotSelectorIcon />}
          </>
        ) : (
          <Typography variant="body4" {...rest}>
            {t(textKeys.noDepots)}
          </Typography>
        )}
      </Box>
    </Box>
  );
};
